/**
 * Boost (reblog) toggle button with optimistic UI. Redirects unauthenticated users to /login.
 * Stores the boost ID locally after creation so it can be deleted without a refetch.
 */
import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Repeat2 } from 'lucide-react'
import { useAuthStore } from '@/store/auth.store'
import { boostsApi } from '@/api'

interface BoostButtonProps {
  postId: string
  boostId?: string | null
  count: number
  isBoosted: boolean
  readOnly?: boolean
}

export function BoostButton({
  postId,
  boostId: initialBoostId,
  count,
  isBoosted: initialIsBoosted,
  readOnly = false,
}: BoostButtonProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()

  const [optimisticBoosted, setOptimisticBoosted] = useState(initialIsBoosted)
  const [optimisticCount, setOptimisticCount] = useState(count)
  const [localBoostId, setLocalBoostId] = useState<string | null>(initialBoostId ?? null)

  const handleClick = async () => {
    if (readOnly) return
    if (!isAuthenticated) {
      navigate(`/login?from=${encodeURIComponent(location.pathname)}`)
      return
    }

    const wasBoostId = localBoostId
    const wasBoosted = optimisticBoosted
    const wasCount = optimisticCount

    // Optimistic update
    setOptimisticBoosted(!wasBoosted)
    setOptimisticCount(wasBoosted ? wasCount - 1 : wasCount + 1)

    try {
      if (!wasBoosted) {
        const result = await boostsApi.boost(postId) as { id: string }
        setLocalBoostId(result.id)
      } else {
        if (wasBoostId) {
          await boostsApi.deleteBoost(wasBoostId)
          setLocalBoostId(null)
        }
      }
    } catch {
      // Revert on error
      setOptimisticBoosted(wasBoosted)
      setOptimisticCount(wasCount)
      setLocalBoostId(wasBoostId)
    }
  }

  const activeColor = 'var(--success)'

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '7px 9px',
        border: 'none',
        background: 'none',
        cursor: readOnly ? 'default' : 'pointer',
        borderRadius: 'var(--radius-pill)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-sm)',
        fontWeight: 600,
        color: optimisticBoosted ? activeColor : 'var(--text-muted)',
        transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => {
        if (readOnly) return
        e.currentTarget.style.background = `color-mix(in srgb, ${activeColor} 12%, transparent)`
      }}
      onMouseLeave={(e) => {
        if (readOnly) return
        e.currentTarget.style.background = 'none'
      }}
    >
      <Repeat2 size={18} />
      {optimisticCount > 0 && <span>{optimisticCount}</span>}
    </button>
  )
}
